import "@/App.css";
import { BrowserRouter, Navigate, Route, Routes, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/AuthContext";
import Login from "@/pages/Login";
import { InstallPrompt } from "@/components/InstallPrompt";
import Dashboard from "@/pages/Dashboard";
import RateFinder from "@/pages/RateFinder";
import QuickAdd from "@/pages/QuickAdd";
import Reorder from "@/pages/Reorder";
import ScanThan from "@/pages/ScanThan";
import ConferenceLayout from "@/conference/ConferenceLayout";
import ConfDashboard from "@/conference/pages/Dashboard";
import ConfOrderSheet from "@/conference/pages/OrderSheet";
import ConfStockSheet from "@/conference/pages/StockSheet";
import ConfOrderSummary from "@/conference/pages/OrderSummary";
import ConfCompanyOrder from "@/conference/pages/CompanyOrder";
import ConfCompanyBalance from "@/conference/pages/BalanceOrder";
import ConfBalanceStock from "@/conference/pages/BalanceStock";
import ConfExtraStock from "@/conference/pages/ExtraStock";
import ConfParties from "@/conference/pages/Parties";
import ConfCompanyOrderSheet from "@/conference/pages/ConfCompanyOrderSheet";
import WholesaleCompanyOrder from "@/conference/pages/WholesaleCompanyOrder";
import Transactions from "@/pages/Transactions";
import ItemMaster from "@/pages/ItemMaster";
import MasterPage from "@/pages/MasterPage";
import Reports from "@/pages/Reports";
import ItemLedger from "@/pages/ItemLedger";
import Hisab from "@/pages/Hisab";
import Users from "@/pages/Users";
import AdminDashboard from "@/pages/AdminDashboard";
import CustomerDashboard from "@/pages/CustomerDashboard";
import PartyLedger from "@/pages/PartyLedger";
import ProfitReport from "@/pages/ProfitReport";
import Vouchers from "@/pages/Vouchers";
import BaleReport from "@/pages/BaleReport";
import ExpenseReport from "@/pages/ExpenseReport";
import PurchaseRegister from "@/pages/PurchaseRegister";
import MillOrders from "@/pages/MillOrders";
import Activity from "@/pages/Activity";

const Protected = ({ children }) => {
  const { user } = useAuth();
  const { pathname } = useLocation();
  if (user === null)
    return (
      <div className="flex min-h-screen items-center justify-center text-sm text-zinc-500">Loading…</div>
    );
  if (!user) return <Login />;
  // rate-only staff can open nothing but the Rate Finder
  if (!user.permissions?.includes("read") && pathname !== "/rate-finder")
    return <Navigate to="/rate-finder" replace />;
  return children;
};

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/" element={<Protected><AdminDashboard /></Protected>} />
            <Route path="/items" element={<Protected><ItemMaster /></Protected>} />
            <Route path="/quick-add" element={<Protected><QuickAdd /></Protected>} />
            <Route path="/reorder" element={<Protected><Reorder /></Protected>} />
            <Route path="/scan" element={<Protected><ScanThan /></Protected>} />
            <Route path="/reports" element={<Protected><Reports /></Protected>} />
            <Route path="/ledger" element={<Protected><ItemLedger /></Protected>} />
            <Route path="/hisab" element={<Protected><Hisab /></Protected>} />
            <Route path="/users" element={<Protected><Users /></Protected>} />
            <Route path="/dashboard" element={<Protected><AdminDashboard /></Protected>} />
            <Route path="/customers" element={<Protected><CustomerDashboard /></Protected>} />
            <Route path="/party-ledger" element={<Protected><PartyLedger /></Protected>} />
            <Route path="/profit-report" element={<Protected><ProfitReport /></Protected>} />
            <Route path="/journal" element={<Protected><Vouchers voucherType="journal" /></Protected>} />
            <Route path="/payment-entry" element={<Protected><Vouchers voucherType="payment" /></Protected>} />
            <Route path="/receipt-entry" element={<Protected><Vouchers voucherType="receipt" /></Protected>} />
            <Route path="/bale-report" element={<Protected><BaleReport /></Protected>} />
            <Route path="/expense-report" element={<Protected><ExpenseReport /></Protected>} />
            <Route path="/purchase-register" element={<Protected><PurchaseRegister /></Protected>} />
            <Route path="/mill-orders" element={<Navigate to="/mill-orders/order" replace />} />
            <Route path="/mill-orders/order" element={<Protected><MillOrders view="all" /></Protected>} />
            <Route path="/mill-orders/pending" element={<Protected><MillOrders view="pending" /></Protected>} />
            <Route path="/mill-orders/dispatch" element={<Protected><MillOrders view="dispatch" /></Protected>} />
            <Route path="/activity" element={<Protected><Activity /></Protected>} />
            <Route path="/inventory" element={<Protected><Dashboard /></Protected>} />
            <Route path="/rate-finder" element={<Protected><RateFinder /></Protected>} />
            <Route path="/conference" element={<Protected><ConferenceLayout /></Protected>}>
              <Route index element={<ConfDashboard />} />
              <Route path="orders" element={<ConfOrderSheet />} />
              <Route path="stock" element={<ConfStockSheet />} />
              <Route path="summary" element={<ConfOrderSummary />} />
              <Route path="company-order" element={<ConfCompanyOrder />} />
              <Route path="company-balance" element={<ConfCompanyBalance />} />
              <Route path="balance-order" element={<ConfCompanyBalance />} />
              <Route path="balance" element={<ConfBalanceStock />} />
              <Route path="extra-stock" element={<ConfExtraStock />} />
              <Route path="parties" element={<ConfParties />} />
              <Route path="conf-company-order" element={<ConfCompanyOrderSheet />} />
              <Route path="wholesale-company-order" element={<WholesaleCompanyOrder />} />
              <Route path="wholesale-balance" element={<ConfCompanyBalance />} />
            </Route>
            <Route path="/masters/:masterKey" element={<Protected><MasterPage /></Protected>} />
            <Route path="/sales" element={<Protected><Transactions configKey="sale" /></Protected>} />
            <Route path="/sales-orders" element={<Protected><Transactions configKey="sales_order" /></Protected>} />
            <Route path="/purchase" element={<Protected><Transactions configKey="purchase" /></Protected>} />
            <Route path="/purchase-orders" element={<Protected><Transactions configKey="purchase_order" /></Protected>} />
            <Route path="/adjustments" element={<Protected><Transactions configKey="adjustment" /></Protected>} />
            <Route path="/transfers" element={<Protected><Transactions configKey="transfer" /></Protected>} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
        <Toaster position="top-right" />
        <InstallPrompt />
      </AuthProvider>
    </div>
  );
}

export default App;
